import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import type { SurveyPhase } from '../fetchSurvey/types';
import type { SurveyAnswers } from '../submitSurvey/types';
import { useSubmitResponse } from '../submitSurvey/useSubmitResponse';
import { useSurveyProgress } from '../surveyProgress/useSurveyProgress';
import { resolveInitialPhase } from '../fetchSurvey/utils/mappers/survey/mapSpecialPages';
import {
  useResetPageStartTime,
  useTrackMaxVisitedPage,
} from './effects';
import { useOnBack, useOnChange, useOnNext, useOnSubmit } from './actions';
import { useTrackPageTime, useTrackOptionalSkips, useCheckBacktrackChanges } from './dispatchers';
import type { SurveyPaginationRefs, SurveyPaginationContext, UseSurveyPaginationProps } from './types';
import { applyMcqDefaults } from './utils/buildInitialAnswers';
import {
  computeCanGoBack,
  computePrimaryButtonType,
  getSpecialPageForPhase,
  resolveContentNavCta,
} from './utils/navigationState';
import { trackSurveyStarted } from '../surveyLogic/telemetry/logicTelemetry';
import { buildLogicContext } from '../surveyLogic/context/buildLogicContext';
import { resolveSkipTarget } from '../surveyLogic/evaluator/evaluateSkipLogics';
import { logOriginSkipTarget } from '../surveyLogic/logger/logicLogger';
import { shouldIncludePageInPath, getVisibleQuestions } from '../surveyLogic/visibility';
import { applyQuestionDisplayVariants } from '../surveyLogic/variants/resolveQuestionDisplayText';
import {
  applyActivePathQuestionNumbers,
  computeActivePathQuestionNumbers,
} from '../surveyProgress/utils/questionNumbers';
import { applyDisplayEmptyPageRelocation } from './utils/applyDisplayEmptyPageRelocation';
import { earnPageOnVisit } from '../surveyProgress/utils/earnedProgress';
import { prepareRenderableQuestions } from './utils/prepareRenderableQuestions';
import { createInitialLogicTrackingRefs } from './utils/logicTrackingRefs';
import { trackQuestionViewed } from '../surveyLogic/telemetry/logicMatchTelemetry';
import { EMPTY_CUSTOM_FIELD_VALUE_MAP } from '../surveyLogic/types/customFieldValues';

export function useSurveyPagination({
  survey,
  session,
  customFieldValues = EMPTY_CUSTOM_FIELD_VALUE_MAP,
}: UseSurveyPaginationProps) {
  const [values, setValues] = useState<SurveyAnswers>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [phase, setPhase] = useState<SurveyPhase>({ kind: 'content', pageIndex: 0 });
  const [progressRevision, setProgressRevision] = useState(0);

  const pages = survey?.pages ?? [];
  const currentPage = pages[currentPageIndex];

  const allQuestions = useMemo(
    () => survey?.pages.flatMap(page => page.questions) ?? [],
    [survey]
  );

  const logicContext = useMemo(
    () => buildLogicContext(values, allQuestions, customFieldValues),
    [values, allQuestions, customFieldValues]
  );

  const currentQuestions = useMemo(() => {
    if (phase.kind !== 'content') return [];
    const rawQuestions = currentPage?.questions ?? [];
    const visible = getVisibleQuestions(rawQuestions, logicContext);
    const withVariants = applyQuestionDisplayVariants(visible, logicContext);
    const numbers = computeActivePathQuestionNumbers(
      pages,
      values,
      allQuestions,
      customFieldValues
    );
    const withNumbers = applyActivePathQuestionNumbers(withVariants, numbers);
    return prepareRenderableQuestions(withNumbers, logicContext);
  }, [
    phase.kind,
    currentPage,
    logicContext,
    pages,
    values,
    allQuestions,
    customFieldValues,
    progressRevision,
  ]);

  const paginationRefs = useRef<SurveyPaginationRefs>({
    pageStartTime: performance.now(),
    sessionStartTime: performance.now(),
    maxVisitedPageIndex: 0,
    validationAttempt: {},
    backtrackSnapshots: {},
    hasLoggedSummary: false,
    validationErrorsCount: 0,
    backtrackCount: 0,
    validationHistory: [],
    visitedPageIndices: new Set([0]),
    skippedPageIndices: new Set(),
    pageHistory: [0],
    earnedQuestionIds: new Set(),
    lastLoggedOriginSkipKey: null,
    ...createInitialLogicTrackingRefs(),
  });

  const bumpProgressRevision = useCallback(() => {
    setProgressRevision(revision => revision + 1);
  }, []);

  const previousPageIndexRef = useRef(currentPageIndex);
  const initializedSurveyIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!survey?.id) return;

    const isNewSurvey = initializedSurveyIdRef.current !== survey.id;
    initializedSurveyIdRef.current = survey.id;

    setValues(prev => applyMcqDefaults(survey.pages, prev));

    if (!isNewSurvey) return;

    setPhase(resolveInitialPhase(survey));
    setCurrentPageIndex(0);
    previousPageIndexRef.current = 0;
    paginationRefs.current.pageHistory = [0];
    paginationRefs.current.skippedPageIndices.clear();
    paginationRefs.current.visitedPageIndices = new Set([0]);
    paginationRefs.current.earnedQuestionIds.clear();
    paginationRefs.current.lastLoggedOriginSkipKey = null;
    Object.assign(paginationRefs.current, createInitialLogicTrackingRefs());
    setProgressRevision(0);
  }, [survey]);

  useEffect(() => {
    if (phase.kind !== 'content' || !survey) return;
    const refs = paginationRefs.current;
    const page = pages[currentPageIndex];
    const pageChanged = previousPageIndexRef.current !== currentPageIndex;
    previousPageIndexRef.current = currentPageIndex;

    const before = refs.earnedQuestionIds.size;
    earnPageOnVisit(refs, page, values, allQuestions, customFieldValues);
    if (refs.earnedQuestionIds.size !== before) {
      bumpProgressRevision();
    }

    if (!pageChanged) return;

    refs.lastLoggedOriginSkipKey = null;
    const lastQuestion = page?.questions?.[page.questions.length - 1];
    if (lastQuestion?.skipLogics?.length) {
      const skipTarget = resolveSkipTarget(
        lastQuestion,
        buildLogicContext(values, allQuestions, customFieldValues),
        pages,
        currentPageIndex,
        { silent: true }
      );
      if (skipTarget) {
        logOriginSkipTarget(lastQuestion.id, skipTarget, currentPageIndex);
        refs.lastLoggedOriginSkipKey = `${currentPageIndex}:${skipTarget.skipLogicId}`;
      }
    }
  }, [currentPageIndex, phase.kind, survey, pages, allQuestions, values, bumpProgressRevision, customFieldValues]);

  const { submit, data: submitData, loading: submitting, error: submitError } = useSubmitResponse(
    session,
    survey
  );

  const earnedQuestionIdsForProgress = useMemo(() => {
    void progressRevision;
    return new Set(paginationRefs.current.earnedQuestionIds);
  }, [progressRevision, currentPageIndex]);

  const { progressPercentage, totalQuestionsCount, answeredQuestionsCount, isComplete } =
    useSurveyProgress({
      pages,
      values,
      allQuestions,
      earnedQuestionIds: earnedQuestionIdsForProgress,
      customFieldValues,
      progressRevision,
    });

  const trackPageTime = useTrackPageTime(paginationRefs);
  const trackOptionalSkips = useTrackOptionalSkips(pages, values);
  const checkBacktrackChanges = useCheckBacktrackChanges(pages, values, paginationRefs);

  const context: SurveyPaginationContext = {
    survey,
    session,
    pages,
    phase,
    setPhase,
    currentPageIndex,
    setCurrentPageIndex,
    values,
    setValues,
    validationErrors,
    setValidationErrors,
    paginationRefs,
    trackPageTime,
    trackOptionalSkips,
    checkBacktrackChanges,
    submit,
    totalQuestionsCount,
    bumpProgressRevision,
    customFieldValues,
  };

  useTrackMaxVisitedPage(context);
  useResetPageStartTime(context);

  const onChange = useOnChange(context);
  const onBack = useOnBack(context);
  const onSubmit = useOnSubmit(context);
  const onNext = useOnNext(context, onSubmit);

  const onStartSurvey = useCallback(() => {
    setPhase({ kind: 'content', pageIndex: 0 });
    setCurrentPageIndex(0);
    paginationRefs.current.pageHistory = [0];
    paginationRefs.current.visitedPageIndices = new Set([0]);
    paginationRefs.current.earnedQuestionIds.clear();
    earnPageOnVisit(paginationRefs.current, pages[0], values, allQuestions, customFieldValues);
    bumpProgressRevision();
    trackSurveyStarted({ source: 'intro' });
  }, [pages, values, allQuestions, bumpProgressRevision, customFieldValues]);

  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  useEffect(() => {
    if (phase.kind !== 'content' || !survey) return;

    const page = pages[currentPageIndex];
    const questions = page?.questions ?? [];
    if (shouldIncludePageInPath(questions, logicContext)) return;

    const refs = paginationRefs.current;
    const relocation = applyDisplayEmptyPageRelocation(
      refs,
      pages,
      currentPageIndex,
      logicContext
    );

    if (!relocation) return;

    if (relocation.action === 'submit') {
      bumpProgressRevision();
      void onSubmitRef.current();
      return;
    }

    setCurrentPageIndex(relocation.pageIndex);
    setPhase({ kind: 'content', pageIndex: relocation.pageIndex });
    bumpProgressRevision();
  }, [phase.kind, currentPageIndex, logicContext, pages, survey, bumpProgressRevision]);

  const canGoBack = useMemo(() => {
    void currentPageIndex;
    return computeCanGoBack(
      survey,
      phase,
      paginationRefs.current.pageHistory.length,
      currentPageIndex
    );
  }, [survey, phase, currentPageIndex]);

  const primaryButtonType = useMemo(
    () => computePrimaryButtonType(survey, phase, currentPageIndex, pages, logicContext),
    [survey, phase, currentPageIndex, pages, logicContext]
  );

  const specialPage = useMemo(
    () => getSpecialPageForPhase(survey, phase),
    [survey, phase]
  );

  const navCta = useMemo(
    () => resolveContentNavCta(survey, currentPageIndex, primaryButtonType),
    [survey, currentPageIndex, primaryButtonType]
  );

  const recordQuestionViewAnalytics = useCallback(
    (questionId: string, pageIndex?: number) => {
      trackQuestionViewed(paginationRefs.current, {
        questionId,
        pageIndex,
      });
    },
    []
  );

  return {
    phase,
    setPhase,
    currentPageIndex,
    values,
    validationErrors,
    currentQuestions,
    pages,
    canGoBack,
    primaryButtonType,
    specialPage,
    navCta,
    onChange,
    onBack,
    onNext,
    onSubmit,
    onStartSurvey,
    progressPercentage,
    totalQuestionsCount,
    answeredQuestionsCount,
    isComplete,
    submitting,
    submitError,
    submitData,
    recordQuestionViewAnalytics,
  };
}
